import { observer } from 'mobx-react-lite'
import { SkeletonListInfiniteScrollTrigger } from '@/components/SkeletonListInfiniteScrollTrigger'
import { ConfirmSheet } from '@/components/ConfirmSheet'
import { type JobDetailsViewModel } from '@/features/jobs/details/JobDetailsViewModel'
import { ContentError } from '@/components/ContentError'
import {
  NoteComposer,
  NotesPane,
  NotesPaneDeleteConfirmContent,
  NotesPaneList,
} from '@/features/jobs/details/components/JobSections/NotesPane'
import { MAX_NOTE_LENGTH } from '@/features/jobs/details/components/JobNotesCard/AddJobNoteDialog/AddJobNoteDialogViewModel'
import { NoteCardContainer } from '@/features/jobs/details/components/JobSections/NoteCard/NoteCardContainer'
import { NoteCardSkeleton } from '@/features/jobs/details/components/JobSections/NoteCard'

export interface NotesPaneContainerProps {
  vm: JobDetailsViewModel
}

export const NotesPaneContainer = observer(function NotesPaneContainer({
  vm,
}: NotesPaneContainerProps) {
  const notesVm = vm.jobNotesVm
  const addNoteVm = notesVm.addNoteVm

  const renderNotes = () => (
    <NotesPaneList
      isEmpty={notesVm.notes.length === 0 && notesVm.scroller.isDone}
    >
      {notesVm.notes.map((note) => (
        <NoteCardContainer key={note.id} vm={notesVm} noteVm={note} />
      ))}
      <SkeletonListInfiniteScrollTrigger
        scroller={notesVm.scroller}
        maxSkeletons={4}
      >
        <NoteCardSkeleton />
      </SkeletonListInfiniteScrollTrigger>
    </NotesPaneList>
  )

  return (
    <NotesPane
      isHighlighted={notesVm.isHighlighted}
      canWrite={notesVm.canWrite}
      composer={
        <NoteComposer
          text={addNoteVm.composerText}
          onTextChange={addNoteVm.setComposerText}
          maxLength={MAX_NOTE_LENGTH}
          charsRemaining={addNoteVm.noteCharsRemaining}
          recipients={addNoteVm.recipients}
          onToggleRecipient={addNoteVm.toggleRecipient}
          canSend={addNoteVm.canSend}
          isSendPending={addNoteVm.sendNote.pending}
          onSend={addNoteVm.sendNote}
          focus$={addNoteVm.focusComposer$}
        />
      }
      isMovedNoticeShowing={notesVm.movedNotice.showing}
      onCloseMovedNotice={notesVm.movedNotice.close}
      deleteConfirmSheet={
        <ConfirmSheet
          danger
          yes="Delete note"
          showing={notesVm.deleteNoteDialogVm.modal.showing}
          isPending={notesVm.deleteNoteDialogVm.delete.pending}
          onConfirm={notesVm.deleteNoteDialogVm.delete}
          onDecline={notesVm.deleteNoteDialogVm.modal.close}
        >
          <NotesPaneDeleteConfirmContent />
        </ConfirmSheet>
      }
    >
      {notesVm.scroller.fetch.match({
        pending: renderNotes,
        rejected: (err) => (
          <ContentError
            size="section"
            title="Error loading notes."
            error={err}
          />
        ),
        resolved: renderNotes,
      })}
    </NotesPane>
  )
})
